import {
  customerFacingAreaLabel,
  customerFacingSafetyRuleLabel,
} from '../../pages/acceptanceProof/workspaceUtils';
import { buyerConditionDisplayName, looksLikeInternalSafetyId } from '../buyerFacing/conditionDisplayNames';
import { OPEN_LINKED_RECORDS_TOOLTIP } from '../incidents/incidentPatternVocabulary';

export const LIVE_VIEW_PAGE_TITLE = 'Camera views';
export const LIVE_VIEW_PAGE_SUBTITLE = 'Live views and supporting evidence from monitored areas.';
export const LIVE_VIEW_HELP_LINE =
  'Live view shows the current camera feed. Annotated evidence shows saved evidence associated with measured safety conditions.';
export const LIVE_VIEW_START_LABEL = 'Start live view';
export const LIVE_VIEW_STOP_LABEL = 'Stop live view';

export const LIVE_VIEW_PATTERN_CARD_TITLE = 'Safety conditions';
export const LIVE_VIEW_PATTERN_CARD_TITLE_PLURAL = 'Safety conditions';

export const OPEN_LINKED_EVENT_RECORDS_LABEL = 'Open linked event records';
export const OPEN_LINKED_EVENT_RECORDS_TOOLTIP = OPEN_LINKED_RECORDS_TOOLTIP;

export const ANNOTATED_VIEW_HELPER =
  'Annotated Evidence shows saved evidence associated with measured safety conditions.';
export const ANNOTATED_OVERLAY_UNAVAILABLE =
  'No annotated evidence is available for this camera yet.';

export const LIVE_VIEW_CAMERA_GRID_FILL_CLASS = 'live-view-camera-grid-fill';

export function liveViewPatternSectionTitle(count: number): string {
  return `${LIVE_VIEW_PATTERN_CARD_TITLE} (${count})`;
}

/** Compact collapsed-row preview of canonical condition names from live data. */
export function liveViewPatternPreviewLine(
  patterns: Array<{
    displayName?: string | null;
    patternLabel?: string | null;
    incidentType?: string | null;
  }>,
): string {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const pattern of patterns || []) {
    const name = liveViewPatternDisplayName(
      pattern.displayName || pattern.patternLabel,
      pattern.incidentType,
    );
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names.join(' · ');
}

export function liveViewCameraStatusLine(statusLabel: string, isLive: boolean): string {
  const status = String(statusLabel || '').trim() || 'Unknown';
  if (/^online$/i.test(status) && isLive) return 'Online · Live';
  return status;
}

const RAW_PATTERN_LABEL_RE = /^(zone violation|incident|unknown pattern)$/i;

/** Prefer API pattern_label; map through the shared Safety-condition catalog. */
export function liveViewPatternDisplayName(
  patternLabel: string | null | undefined,
  incidentType?: string | null,
): string {
  const label = String(patternLabel || '').trim();
  const type = String(incidentType || '').trim();
  const compact = label.includes('·') ? label.split('·')[0].trim() : label;
  const catalogued = buyerConditionDisplayName(compact, type);
  if (catalogued && catalogued !== 'Safety condition') return catalogued;
  if (compact && !RAW_PATTERN_LABEL_RE.test(compact) && !looksLikeIncidentTypeSlug(compact, type)) {
    return compact;
  }
  const fromType = customerFacingSafetyRuleLabel(type || compact);
  if (fromType && fromType !== 'Safety event') {
    return buyerConditionDisplayName(fromType, type);
  }
  return compact || 'Safety condition';
}

export function operatorFacingAreaLabel(
  patternLabel?: string | null,
  groupKey?: string | null,
  zoneLabel?: string | null,
): string | null {
  const raw =
    String(zoneLabel || '').trim() ||
    parseZoneFromGroupKey(groupKey) ||
    parseZoneFromPatternLabel(patternLabel) ||
    '';
  if (!raw) return null;
  if (looksLikeInternalSafetyId(raw) || /^[0-9a-f]{8,}$/i.test(raw)) return null;
  const area = customerFacingAreaLabel(raw) || liveViewPatternAreaLabel(patternLabel, groupKey) || raw;
  if (!area) return null;
  if (looksLikeInternalSafetyId(area) || /^[0-9a-f]{8,}$/i.test(area)) return null;
  return area;
}

function looksLikeIncidentTypeSlug(label: string, incidentType: string): boolean {
  if (!incidentType) return false;
  const normLabel = label.toLowerCase().replace(/\s+/g, '_');
  return normLabel === incidentType.toLowerCase();
}

export function parseZoneFromGroupKey(groupKey?: string | null): string | null {
  const parts = String(groupKey || '')
    .split('|')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) return parts[1] || null;
  return null;
}

export function parseZoneFromPatternLabel(patternLabel?: string | null): string | null {
  const text = String(patternLabel || '').trim();
  if (!text.includes('·')) return null;
  const zone = text.split('·').pop()?.trim();
  return zone || null;
}

export function liveViewPatternAreaLabel(
  patternLabel?: string | null,
  groupKey?: string | null,
): string {
  const zoneRaw =
    parseZoneFromGroupKey(groupKey) || parseZoneFromPatternLabel(patternLabel) || '';
  return customerFacingAreaLabel(zoneRaw) || zoneRaw;
}

export function liveViewPatternRuleSecondary(
  incidentType?: string | null,
  patternLabel?: string | null,
  groupKey?: string | null,
  zoneLabel?: string | null,
): string {
  const rule = String(incidentType || '').trim() || 'unknown';
  const area =
    String(zoneLabel || '').trim() ||
    liveViewPatternAreaLabel(patternLabel, groupKey) ||
    parseZoneFromGroupKey(groupKey) ||
    parseZoneFromPatternLabel(patternLabel) ||
    '—';
  return `Rule: ${rule} · Area: ${area}`;
}

/** Flat incident queue scoped to the same filters as Risk review. */
export function buildViewEventRecordsPath(linkToPatternReview: string): string | null {
  const raw = String(linkToPatternReview || '').trim();
  if (!raw) return null;
  try {
    const url = raw.startsWith('/')
      ? new URL(raw, 'https://live-view.local')
      : new URL(raw);
    url.searchParams.set('table_mode', 'raw');
    return url.pathname + (url.search ? url.search : '');
  } catch {
    return null;
  }
}
